import { useRef, useState } from 'react';
import {
  FileAudio,
  Loader2,
  Play,
  Square,
  Upload,
  X,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useAuthStore } from '@/lib/auth/store';
import { cn } from '@/lib/utils/cn';
import { useUploadPrompt } from '../api';
import {
  CATEGORY_LABEL,
  PROMPT_CATEGORIES,
  type PromptCategory,
} from '../types';

interface Props {
  onSuccess?: () => void;
}

/** Browser-side helper to figure out the local file duration without
 *  uploading first — used in the preview row so the user knows roughly how
 *  long their file is. */
function useLocalAudioPreview() {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [playing, setPlaying] = useState(false);
  const [duration, setDuration] = useState(0);

  const reset = () => {
    audioRef.current?.pause();
    audioRef.current = null;
    setPlaying(false);
    setDuration(0);
  };

  const load = (file: File) => {
    reset();
    const url = URL.createObjectURL(file);
    const a = new Audio(url);
    a.addEventListener('loadedmetadata', () => {
      setDuration(Number.isFinite(a.duration) ? a.duration : 0);
    });
    a.addEventListener('ended', () => setPlaying(false));
    audioRef.current = a;
  };

  const toggle = () => {
    if (!audioRef.current) return;
    if (playing) {
      audioRef.current.pause();
      setPlaying(false);
    } else {
      void audioRef.current.play();
      setPlaying(true);
    }
  };

  return { playing, duration, load, toggle, reset };
}

function formatDuration(s: number): string {
  if (!Number.isFinite(s) || s <= 0) return '—';
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${String(sec).padStart(2, '0')}`;
}

export function UploadAudioPanel({ onSuccess }: Props) {
  const userId = useAuthStore((s) => s.userId);
  const upload = useUploadPrompt();
  const preview = useLocalAudioPreview();

  const [file, setFile] = useState<File | null>(null);
  const [fileName, setFileName] = useState('');
  const [category, setCategory] = useState<PromptCategory | ''>('');
  const [dragOver, setDragOver] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const acceptFile = (f: File) => {
    const ext = f.name.toLowerCase().split('.').pop() ?? '';
    if (ext !== 'wav' && ext !== 'mp3') {
      toast.error('Only .wav or .mp3 files are supported.');
      return;
    }
    setFile(f);
    if (!fileName) {
      // Auto-fill — strip the extension and any path bits.
      setFileName(f.name.replace(/\.[^.]+$/, ''));
    }
    preview.load(f);
  };

  const onPick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (f) acceptFile(f);
    e.target.value = '';
  };

  const clearFile = () => {
    setFile(null);
    preview.reset();
  };

  const onDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files?.[0];
    if (f) acceptFile(f);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!file) return toast.error('Pick an audio file.');
    if (!fileName.trim()) return toast.error('Give the file a name.');
    if (!category) return toast.error('Pick a category.');

    const ext = (file.name.toLowerCase().split('.').pop() ?? 'wav') as
      | 'wav'
      | 'mp3';

    upload.mutate(
      {
        waveFile: file,
        userId: Number(userId),
        fileName: fileName.trim(),
        promptCategory: category,
        fileType: ext,
      },
      {
        onSuccess: (res) => {
          toast.success(res?.message ?? 'Prompt uploaded');
          clearFile();
          setFileName('');
          setCategory('');
          onSuccess?.();
        },
        onError: () => toast.error('Could not upload the prompt.'),
      },
    );
  };

  return (
    <form className="space-y-4" onSubmit={handleSubmit}>
      {/* Drag-drop zone */}
      <div
        role="button"
        tabIndex={0}
        onClick={() => fileInputRef.current?.click()}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            fileInputRef.current?.click();
          }
        }}
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        className={cn(
          'flex cursor-pointer flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed bg-muted/30 px-4 py-8 text-center transition-colors',
          dragOver
            ? 'border-primary bg-primary/5'
            : 'border-border hover:border-primary/40',
        )}
      >
        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-primary">
          <Upload className="h-5 w-5" />
        </div>
        <div className="text-sm font-semibold text-foreground">
          Drop a WAV or MP3 file
        </div>
        <p className="text-xs text-muted-foreground">
          or click to browse from your computer
        </p>
        <input
          ref={fileInputRef}
          type="file"
          accept=".wav,.mp3,audio/wav,audio/mpeg"
          className="hidden"
          onChange={onPick}
        />
      </div>

      {/* File preview */}
      {file && (
        <div className="flex items-center gap-3 rounded-lg border border-border bg-card p-3">
          <Button
            type="button"
            variant="outline"
            size="icon"
            className="h-9 w-9 shrink-0 rounded-full"
            onClick={preview.toggle}
            aria-label={preview.playing ? 'Pause preview' : 'Play preview'}
          >
            {preview.playing ? (
              <Square className="h-4 w-4" />
            ) : (
              <Play className="h-4 w-4" />
            )}
          </Button>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1.5">
              <FileAudio className="h-3.5 w-3.5 text-muted-foreground" />
              <span className="truncate text-sm font-medium text-foreground">
                {file.name}
              </span>
            </div>
            <div className="mt-0.5 text-[11px] text-muted-foreground">
              {(file.size / 1024).toFixed(0)} KB ·{' '}
              {formatDuration(preview.duration)}
            </div>
          </div>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={clearFile}
            aria-label="Remove file"
          >
            <X className="h-3.5 w-3.5" />
          </Button>
        </div>
      )}

      {/* Metadata */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label htmlFor="prompt-name">
            Display name
            <span className="ml-1 text-destructive">*</span>
          </Label>
          <Input
            id="prompt-name"
            value={fileName}
            onChange={(e) => setFileName(e.target.value)}
            placeholder="e.g. welcome-en-male"
          />
        </div>
        <div className="space-y-1.5">
          <Label>
            Category
            <span className="ml-1 text-destructive">*</span>
          </Label>
          <Select
            value={category}
            onValueChange={(v) => setCategory(v as PromptCategory)}
          >
            <SelectTrigger>
              <SelectValue placeholder="Pick a category" />
            </SelectTrigger>
            <SelectContent>
              {PROMPT_CATEGORIES.map((c) => (
                <SelectItem key={c} value={c}>
                  {CATEGORY_LABEL[c]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="flex items-center justify-end gap-2">
        <Button type="submit" disabled={upload.isPending}>
          {upload.isPending ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Upload className="h-4 w-4" />
          )}
          Upload prompt
        </Button>
      </div>
    </form>
  );
}
